import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Form } from 'formik';
import { ArrowRight, Eye, EyeOff } from 'lucide-react';
import { LogoLoader, TwoFactorVerify } from '@gokrew/ui';
import {
  clearSsoParams,
  decodeAccessTokenClaims,
  isTerminalAuthError,
  readSsoRedirect,
  SsoButtons,
  SsoNotice,
  TerminalAuthError,
} from '@gokrew/ui/auth';
import { SSO_PROVIDER_LABEL } from '@gokrew/models/sso';
import { FormField, FormikWithSchema } from '@gokrew/ui/form';
import { getApiErrorMessage } from '@gokrew/ui/utility/utility';
import { getConfig } from '@gokrew/ui/runtime-config';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import {
  getLinkedCompanies,
  isOtpChallengeResponse,
  login,
  resendOtp,
  verifyOtp,
} from '@/api/auth';
import type { AuthTokensResponse, LoginApiResponse } from '@/types/auth';
import { loginSchema } from '@/validation/authSchema';
import { useAuth } from '@/context/AuthContext';
import { tokenManager } from '@/auth';
import { Button } from '@/components/ui/CustomButton';
import { AuthV2Layout } from '../components/AuthV2Layout';
import layoutStyles from '../components/AuthV2Layout.module.css';
import { cn } from '@gokrew/ui/lib/utils';
import styles from './LoginPage.module.css';

function isSafeInternalRedirect(redirect: string | null): redirect is string {
  return (
    !!redirect &&
    redirect.startsWith('/') &&
    !redirect.startsWith('//') &&
    !redirect.includes('\\')
  );
}

export function LoginV2Page() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAuth } = useAuth();
  const { t } = useTranslation(TranslationNS.Auth);
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [pendingAuth, setPendingAuth] = useState<{
    maskedEmail: string;
    otpToken: string;
  } | null>(null);
  const [lastEmail, setLastEmail] = useState('');
  const employeePortalUrl = getConfig().EMPLOYEE_PORTAL_URL;
  const { t: tSso } = useTranslation(TranslationNS.Sso);
  // Google / Microsoft sign-in came back here (D199): `sso=complete` means
  // the callback set the refresh cookie, `sso_error` why it refused.
  const [sso] = useState(() => readSsoRedirect(window.location.search));
  const [ssoCompleting, setSsoCompleting] = useState(sso.complete);
  const [ssoError, setSsoError] = useState(
    sso.error
      ? { code: sso.error, provider: sso.provider, email: sso.email }
      : null
  );
  const redirectParam = searchParams.get('redirect');

  async function completeLogin(data: AuthTokensResponse) {
    setAuth(data);

    const redirect = searchParams.get('redirect');
    if (isSafeInternalRedirect(redirect)) {
      window.location.replace(redirect);
      return;
    }

    try {
      const resp = await getLinkedCompanies();
      navigate(resp.data.length > 0 ? '/dashboard' : '/add-company', {
        replace: true,
      });
    } catch {
      navigate('/add-company', { replace: true });
    }
  }

  useEffect(() => {
    if (!sso.complete && !sso.error) return;
    clearSsoParams();
    if (!sso.complete) return;
    let cancelled = false;
    tokenManager.forceRefresh().then((accessToken) => {
      if (cancelled) return;
      if (!accessToken) {
        setSsoCompleting(false);
        setSsoError({ code: 'expired', provider: null, email: null });
        return;
      }
      if (sso.linked)
        toast.success(
          tSso('linked_toast', { provider: SSO_PROVIDER_LABEL[sso.linked] })
        );
      const claims = decodeAccessTokenClaims(accessToken);
      completeLogin({
        message: '',
        data: {
          accessToken,
          sessionId: claims.sessionId ?? '',
          role: claims.role as AuthTokensResponse['data']['role'],
        },
      });
    });
    return () => {
      cancelled = true;
    };
    // Runs once for the sign-in that landed on this page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mutation = useMutation<
    LoginApiResponse,
    AxiosError<{ message: string }>,
    { email: string; password: string }
  >({
    mutationFn: ({ email, password }) => login(email, password),
    onSuccess: (data, { email }) => {
      setLastEmail(email);
      if (isOtpChallengeResponse(data)) {
        setPendingAuth({
          maskedEmail: data.data.maskedEmail,
          otpToken: data.data.otpToken,
        });
      } else {
        // 2FA disabled server-side (or the device is already trusted) —
        // tokens come back directly, no OTP step.
        completeLogin(data);
      }
    },
    onError: (error) => {
      setServerError(
        getApiErrorMessage(error, t('signup_v2.login_screen.server_error'))
      );
    },
  });

  async function handleVerify(code: string): Promise<void> {
    if (!pendingAuth) return;
    try {
      const result = await verifyOtp(pendingAuth.otpToken, code);
      await completeLogin(result);
    } catch (err) {
      const message = getApiErrorMessage(
        err,
        t('signup_v2.two_factor_screen.invalid_code')
      );
      throw isTerminalAuthError(err)
        ? new TerminalAuthError(message)
        : new Error(message);
    }
  }

  async function handleResendCode(): Promise<void> {
    if (!pendingAuth) return;
    try {
      await resendOtp(pendingAuth.otpToken);
    } catch (err) {
      const message = getApiErrorMessage(
        err,
        t('signup_v2.login_screen.server_error')
      );
      throw isTerminalAuthError(err)
        ? new TerminalAuthError(message)
        : new Error(message);
    }
  }

  if (pendingAuth) {
    return (
      <AuthV2Layout>
        <div className={layoutStyles.formInner}>
          <div className={layoutStyles.formCard}>
            <TwoFactorVerify
              email={pendingAuth.maskedEmail}
              onVerify={handleVerify}
              onResend={handleResendCode}
              onBack={() => setPendingAuth(null)}
            />
          </div>
        </div>
      </AuthV2Layout>
    );
  }

  if (ssoCompleting) {
    return (
      <AuthV2Layout>
        <div className={layoutStyles.formInner}>
          <div className={cn(layoutStyles.formCard, styles.ssoCompleting)}>
            <LogoLoader size="sm" />
            <p className={styles.subtext}>{tSso('completing')}</p>
          </div>
        </div>
      </AuthV2Layout>
    );
  }

  return (
    <AuthV2Layout>
      <div className={layoutStyles.formInner}>
        <div className={layoutStyles.formCard}>
          <h2 className={styles.heading}>
            {t('signup_v2.login_screen.heading')}
          </h2>
          <p className={styles.subtext}>
            {t('signup_v2.login_screen.subtext')}
          </p>

          {ssoError && (
            <SsoNotice
              code={ssoError.code}
              provider={ssoError.provider}
              email={ssoError.email}
            />
          )}
          <SsoButtons
            portal="company"
            returnTo={
              isSafeInternalRedirect(redirectParam) ? redirectParam : null
            }
          />

          <FormikWithSchema
            initialValues={{ email: lastEmail, password: '', remember: false }}
            validationSchema={loginSchema(t)}
            onSubmit={(values, { setSubmitting }) => {
              setServerError(null);
              mutation.mutate({
                email: values.email,
                password: values.password,
              });
              setSubmitting(false);
            }}
          >
            {({ isValid, dirty }) => (
              <Form className={styles.form} noValidate>
                <FormField
                  name="email"
                  type="email"
                  label={t('signup_v2.login_screen.email_label')}
                  placeholder={t('signup_v2.login_screen.email_placeholder')}
                  autoFocus
                />

                <div>
                  <FormField
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    label={t('signup_v2.login_screen.password_label')}
                    placeholder={t(
                      'signup_v2.login_screen.password_placeholder'
                    )}
                    IconSuffix={showPassword ? Eye : EyeOff}
                    onIconClick={() => setShowPassword((s) => !s)}
                  />
                  <div className={styles.forgotRow}>
                    <Link to="/forgot-password" className={styles.forgotLink}>
                      {t('signup_v2.login_screen.forgot_password')}
                    </Link>
                  </div>
                </div>

                {serverError && (
                  <p className={styles.serverError}>{serverError}</p>
                )}

                <Button
                  type="submit"
                  className={styles.submitBtn}
                  IconSuffix={mutation.isPending ? undefined : ArrowRight}
                  disabled={mutation.isPending || !isValid || !dirty}
                >
                  {mutation.isPending ? (
                    <LogoLoader size="sm" />
                  ) : (
                    t('signup_v2.login_screen.submit')
                  )}
                </Button>
              </Form>
            )}
          </FormikWithSchema>

          <p className={styles.signUpRow}>
            {t('signup_v2.login_screen.no_account')}{' '}
            <Link to="/signup" className={styles.signUpLink}>
              {t('signup_v2.login_screen.sign_up_link')}
            </Link>
          </p>

          {/* Static on purpose: a failed sign-in never says which portal the
              account belongs to, so point employees to theirs up front. */}
          {employeePortalUrl && (
            <p className={styles.portalHint}>
              {t('signup_v2.login_screen.employee_portal_hint')}{' '}
              <a href={employeePortalUrl} className={styles.signUpLink}>
                {t('signup_v2.login_screen.employee_portal_link')}
              </a>
            </p>
          )}
        </div>
      </div>
    </AuthV2Layout>
  );
}
